import { collection, limit, orderBy, query, where, type Unsubscribe } from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { mapDocs } from '@utils/firestore';
import { FundingRequestSchema } from '../schemas/firestoreSchemas';
import type { FundingRequest } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:fundingRequests');

// Funding requests (Oct 2026, Redi bucks): fundingRequests/{planId__bundleId__bidId}.
// No client writes them. A plan owner reads their own and moves them through
// the requestFunding callable; a moderator reads the area's and decides
// through reviewFundingRequest (functions/src/funding/).

const REQUEST_PAGE = 300;

export type FundingAction = 'approve' | 'decline' | 'cancel' | 'release' | 'withdraw';

export interface RequestFundingInput {
  planId: string;
  bundleId: string;
  bidId: string;
  amount: number;
}

export interface ReviewFundingInput {
  requestId: string;
  action: FundingAction;
  fundId?: string;
  note?: string;
}

function requestsCollection() {
  return collection(db, COLLECTIONS.FUNDING_REQUESTS);
}

const FUNDING_ERROR_FALLBACK = "That didn't go through. Try again in a moment.";

/**
 * Plain copy for a failed callable. The functions word their preconditions for
 * people ("That fund doesn't have RB 12,000 available."), so those pass through.
 */
export function toFundingError(error: unknown): string {
  const code = (error as { code?: string } | null)?.code ?? '';
  const message = (error as { message?: string } | null)?.message ?? '';
  const bare = code.replace(/^functions\//, '');
  const worded = [
    'invalid-argument',
    'not-found',
    'failed-precondition',
    'already-exists',
    'permission-denied',
    'resource-exhausted',
  ];
  return worded.includes(bare) && message ? message : FUNDING_ERROR_FALLBACK;
}

export const fundingRequestsApi = {
  /** Every request in the area, newest first. Moderators only. */
  subscribeAreaRequests(
    areaId: string,
    onNext: (requests: FundingRequest[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(
        requestsCollection(),
        where('areaId', '==', areaId),
        orderBy('requestedAt', 'desc'),
        limit(REQUEST_PAGE)
      ),
      (snap) => onNext(mapDocs(snap, FundingRequestSchema)),
      (error) => {
        log.warn('subscribeAreaRequests failed:', error);
        onError?.(error);
      }
    );
  },

  /** The owner's requests on one plan. ownerId makes the query provable in rules. */
  subscribeOwnerRequests(
    planId: string,
    uid: string,
    onNext: (requests: FundingRequest[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(
        requestsCollection(),
        where('ownerId', '==', uid),
        where('planId', '==', planId),
        orderBy('requestedAt', 'desc'),
        limit(REQUEST_PAGE)
      ),
      (snap) => onNext(mapDocs(snap, FundingRequestSchema)),
      (error) => {
        log.warn('subscribeOwnerRequests failed:', error);
        onError?.(error);
      }
    );
  },

  async requestFunding(input: RequestFundingInput): Promise<{ ok: true; requestId: string }> {
    const callable = httpsCallable<RequestFundingInput, { ok: true; requestId: string }>(
      functions,
      'requestFunding'
    );
    return (await callable(input)).data;
  },

  async reviewFundingRequest(input: ReviewFundingInput): Promise<{ ok: true; status: string }> {
    const callable = httpsCallable<ReviewFundingInput, { ok: true; status: string }>(
      functions,
      'reviewFundingRequest'
    );
    return (await callable(input)).data;
  },
};
